import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { ContentSection, LocalizedDate, TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $itemGroupForm,
  ItemGroupFormState,
} from '../../../_state/itemGroupForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { ItemGroupRecord } from '../../../types/inventoryItem';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectItemGroup } from '~/shared/components/select-fields/SelectItemGroup';

type ItemGroupFormFieldsProps = {
  disabled?: boolean;
  showTimestamps?: boolean;
  itemGroup?: ItemGroupRecord | null;
  className?: string;
};

const formSelector = (state: ItemGroupFormState) => ({
  name: state.name,
  number: state.number,
  parentId: state.parentId,
  description: state.description,
  notes: state.notes,
  defaultPricingUnit: state.defaultPricingUnit,
});

export const ItemGroupFormFields = ({
  disabled = false,
  showTimestamps = false,
  itemGroup,
  className,
}: ItemGroupFormFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($itemGroupForm, formSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($itemGroupForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof ItemGroupFormState>(field: K) =>
      (value: ItemGroupFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  return (
    <div className={cn('@ItemGroupFormFields', className)}>
      <ContentSection
        title={
          t.inventoryView.itemGroupDetailView.sections.itemGroupInformation
        }
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.inventoryView.itemGroupCreateDialog.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders.name
            }
            isRequired
            isDisabled={disabled}
          />

          <TextField
            label={t.inventoryView.itemGroupCreateDialog.fields.number}
            value={form.number}
            onChange={(value) => handleFieldChange('number')(value)}
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders.number
            }
            isRequired
            isDisabled={disabled}
          />

          <SelectItemGroup
            label={t.inventoryView.itemGroupCreateDialog.fields.parentGroup}
            value={form.parentId}
            onChange={(value) => handleFieldChange('parentId')(value)}
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders.parentGroup
            }
            isDisabled={disabled}
          />

          <SelectEnum<Unit>
            label={
              t.inventoryView.itemGroupCreateDialog.fields.defaultPricingUnit
            }
            enumValue={enumUnit}
            value={form.defaultPricingUnit as Unit | undefined}
            onChange={(value) =>
              handleFieldChange('defaultPricingUnit')(value ?? undefined)
            }
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders
                .defaultPricingUnit
            }
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      {showTimestamps && (
        <ContentSection
          title={t.inventoryView.itemGroupDetailView.sections.timestamps}
          variant="panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemGroupDetailView.fields.created}
              </span>
              <span className="text-sm font-medium">
                {itemGroup?.timestamps.createdAt ? (
                  <LocalizedDate date={itemGroup.timestamps.createdAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemGroupDetailView.fields.updated}
              </span>
              <span className="text-sm font-medium">
                {itemGroup?.timestamps.updatedAt ? (
                  <LocalizedDate date={itemGroup.timestamps.updatedAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.inventoryView.itemGroupDetailView.sections.additional}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4">
          <TextField
            label={t.inventoryView.itemGroupCreateDialog.fields.description}
            value={form.description}
            onChange={(value) => handleFieldChange('description')(value)}
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders.description
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.inventoryView.itemGroupCreateDialog.fields.notes}
            value={form.notes}
            onChange={(value) => handleFieldChange('notes')(value)}
            placeholder={
              t.inventoryView.itemGroupCreateDialog.placeholders.notes
            }
            textArea
            isDisabled={disabled}
          />
        </div>
      </ContentSection>
    </div>
  );
};
